import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Globe, Check } from "lucide-react";
import { LANGUAGES } from "@/i18n";

const GT_KEY = "wo_gt";

function setGoogleCookie(code) {
  const val = `/en/${code}`;
  const host = window.location.hostname;
  document.cookie = `googtrans=${val};path=/`;
  document.cookie = `googtrans=${val};path=/;domain=${host}`;
  document.cookie = `googtrans=${val};path=/;domain=.${host}`;
}

function clearGoogleCookie() {
  const host = window.location.hostname;
  const exp = "expires=Thu, 01 Jan 1970 00:00:00 GMT";
  document.cookie = `googtrans=;path=/;${exp}`;
  document.cookie = `googtrans=;path=/;domain=${host};${exp}`;
  document.cookie = `googtrans=;path=/;domain=.${host};${exp}`;
}

export default function LanguageSwitcher({ compact = false, testId = "language-switcher", dropUp = false, showName = false, label = "" }) {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const activeGt = typeof window !== "undefined" ? localStorage.getItem(GT_KEY) : null;
  const selectedCode = activeGt || i18n.language;
  const current =
    LANGUAGES.find((l) => l.code === selectedCode) || LANGUAGES[0];

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const pick = (l) => {
    setOpen(false);
    if (l.code === selectedCode) return;
    if (l.native) {
      const wasGt = localStorage.getItem(GT_KEY);
      localStorage.removeItem(GT_KEY);
      i18n.changeLanguage(l.code);
      if (wasGt) { clearGoogleCookie(); window.location.reload(); }
    } else {
      localStorage.setItem(GT_KEY, l.code);
      i18n.changeLanguage("en");
      setGoogleCookie(l.code);
      window.location.reload();
    }
  };

  return (
    <div className="relative notranslate flex items-center gap-2" translate="no" ref={ref}>
      {label && <span className="text-xs font-medium text-slate-400 whitespace-nowrap">{label}</span>}
      <button onClick={() => setOpen(!open)} data-testid={testId}
        className="flex items-center gap-1.5 px-2.5 py-2 rounded-lg text-slate-300 hover:text-teal-300 hover:bg-white/5 transition-colors">
        <Globe size={17} />
        {!compact && <span className="text-sm font-medium">{current.flag}</span>}
        {showName && <span className="text-sm font-medium text-slate-200">{current.label}</span>}
      </button>

      {open && (
        <div style={{ position: "absolute" }} className={`right-0 ${dropUp ? "bottom-full mb-2" : "top-full mt-2"} w-52 max-h-[min(60vh,22rem)] overflow-y-auto liquid-glass rounded-xl z-[70] py-1`} data-testid="language-menu">
          {LANGUAGES.map((l) => (
            <button key={l.code} onClick={() => pick(l)} data-testid={`lang-${l.code}`}
              className={`w-full flex items-center gap-2.5 px-3.5 py-2 text-sm transition-colors ${selectedCode === l.code ? "text-teal-300 bg-teal-500/10" : "text-slate-200 hover:bg-white/5"}`}>
              <span className="text-base">{l.flag}</span>
              <span className="flex-1 text-left">{l.label}</span>
              {selectedCode === l.code && <Check size={14} />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
